import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { appData } from '@/lib/data';
import {
  getCurrentFrame,
  getPublishedEntityLocation,
  hasVocamData,
  processQuestion,
  replaceContentAssetLink,
  useIsMobileScreenSize,
} from '@/lib/util';
import LiveRoutes, { LiveParamType } from './routes';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import {
  useGetManifestQuery,
  useGetSignedCookiesQuery,
  useGetElearningJsonQuery,
  useGetQuizJsonQuery,
  useGetHelpJsonQuery,
  useGetElearningBehaviourSettingsQuery,
  useGetVideoPreviewLinkQuery,
  useStartLiveQuizAttemptMutation,
  useEvaluateLiveQuizAttemptMutation,
  useGetLiveQuizAttemptQuery,
} from '@/modules/services';
import { FaqType, HelperType } from '@/modules/types/helper';
import PlayerUi from '@/components/Player';
import { AnswerDataType } from '@/modules/types/question';
import { useSubmitQuizResultLiveMutation } from '@/modules/services/quizzes';
import {
  LiveAssessmentTrackingType,
  QuizAttemptResponseType,
  QuizAttemptStartResponseType,
} from '@/modules/types/quiz';
import { Spinner } from 'elmo-elements';
import { useResumeElearning } from '@/modules/hooks/useResumeElearning';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';
import { useFlags } from 'launchdarkly-react-client-sdk';

const Live = (): ReactElement => {
  const { tenantId, elearningId, type, themeId, frameId } =
    useParams<LiveParamType>();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const cid = query.get('cid') ?? undefined;
  const mid = query.get('mid') ?? undefined;
  const token = query.get('token') ?? undefined;
  const history = useHistory();
  const { learningElaQuizRetryLimit20250128: isQuizAttemptEnabled } =
    useFlags();
  const initialFrameId = useRef(frameId);
  const [currentFrameId, setCurrentFrameId] = useState(frameId || '');
  const [isQuizReady, setIsQuizReady] = useState(false);
  const [quizStartAttemptInfo, setQuizStartAttemptInfo] =
    useState<QuizAttemptStartResponseType | null>(null);

  const [submitQuizResult, { error, isLoading }] =
    useSubmitQuizResultLiveMutation();
  const [
    startQuizAttempt,
    {
      error: errorQuizAttempt,
      isLoading: isLoadingQuizAttempt,
      data: quizStartAttemptData,
    },
  ] = useStartLiveQuizAttemptMutation();
  const [
    evaluateQuizAttempt,
    {
      error: errorEvaluateQuizAttempt,
      isLoading: isLoadingEvaluateQuizAttempt,
      data: quizEvaluateAttemptData,
    },
  ] = useEvaluateLiveQuizAttemptMutation();
  const {
    data: elearningBehaviourSettingsData,
    isLoading: elearningBehaviourSettingsIsLoading,
  } = useGetElearningBehaviourSettingsQuery();
  const isBookmarkSettingsEnabled =
    elearningBehaviourSettingsData?.elearningBookmarkEnable;

  const { data: manifestData } = useGetManifestQuery(
    {
      type,
      tenantId,
      elearningId,
    },
    {
      skip: !cid || !mid || !token,
    },
  );

  const { isSuccess } = useGetSignedCookiesQuery(
    {
      tenantId,
      type,
      elearningId,
      ...(manifestData?.Item?.elearningIdentifier && {
        elearningIdentifier: `${manifestData?.Item?.elearningIdentifier}`,
      }),
      ...(manifestData?.Item?.quizIdentifier && {
        quizIdentifier: `${manifestData?.Item?.quizIdentifier}`,
      }),
      ...(manifestData?.Item?.elearningIdentifier && {
        themeId: `${manifestData?.Item?.themeUUID}`,
      }),
      cid: Number(cid),
      mid: Number(mid),
      token,
    },
    { skip: !manifestData?.Item },
  );

  const { data: elearningData } = useGetElearningJsonQuery(
    {
      tenantId,
      type,
      entityId: elearningId,
      identifier: manifestData?.Item?.elearningIdentifier,
      entity: 'elearnings',
      contentType: 'published-content',
    },
    { skip: !isSuccess || !manifestData?.Item },
  );

  const { data: quizData, isLoading: quizIsLoading } = useGetQuizJsonQuery(
    {
      tenantId,
      type,
      entityId: elearningId,
      identifier: manifestData?.Item?.elearningIdentifier,
      entity: 'quizzes',
      contentType: 'published-content',
    },
    { skip: !isSuccess || !manifestData?.Item },
  );
  const { data: liveQuizAttemptData } = useGetLiveQuizAttemptQuery(
    {
      moduleId: mid ? Number(mid) : 0,
      courseId: cid ? Number(cid) : 0,
      attemptLimit: quizData?.quizMetadata?.attemptLimit ?? 0,
    },
    {
      skip:
        !mid ||
        !cid ||
        !quizData?.quizMetadata.lmsTrackingEnable ||
        !quizData ||
        quizIsLoading,
    },
  );

  const showResumeModal =
    initialFrameId.current && isBookmarkSettingsEnabled && !isQuizReady;
  // passing undefined queryArgs as we don't need them but need to pass the queryOptions to skip requests until we have signed cookies
  const { data: helpData } = useGetHelpJsonQuery(undefined, {
    skip: !isSuccess,
  });

  const currentFrame = useMemo(
    () => getCurrentFrame(elearningData?.frames ?? [], currentFrameId),
    [elearningData?.frames, currentFrameId],
  );

  const { data: vocamVideoLink } = useGetVideoPreviewLinkQuery(
    {
      moduleId: currentFrame?.vocamMetadata.moduleId as number,
      topicId: currentFrame?.vocamMetadata.topicId as number,
    },
    {
      skip:
        !currentFrame?.vocamMetadata.moduleId ||
        !currentFrame?.vocamMetadata.topicId ||
        !hasVocamData(currentFrame),
    },
  );

  useThemeStyle(elearningData?.theme);
  useModuleStyle(elearningData);

  useEffect(() => {
    if (!elearningBehaviourSettingsIsLoading && !showResumeModal) {
      setCurrentFrameId('');
    }
  }, [elearningBehaviourSettingsIsLoading, showResumeModal]);

  useEffect(() => {
    if (
      elearningData?.frames &&
      elearningData.frames.length > 0 &&
      frameId &&
      !elearningData.frames.some((f) => f.id === frameId)
    ) {
      const firstFrameId = elearningData.frames[0].id;
      setCurrentFrameId(firstFrameId);
      history.replace(
        `${generatePath(LiveRoutes.path, {
          tenantId,
          elearningId,
          type,
          frameId: firstFrameId,
        })}?${query.toString()}`,
      );
    }
  }, [elearningData?.frames, frameId]);

  useEffect(() => {
    if (isLoadingQuizAttempt || errorQuizAttempt) return;

    if (quizStartAttemptData) {
      setQuizStartAttemptInfo(quizStartAttemptData);
      // this will show the quiz. Only show when we have successfully submit start attempt
      setIsQuizReady(true);
    }
  }, [isLoadingQuizAttempt, errorQuizAttempt, quizStartAttemptData]);

  useEffect(() => {
    if (
      !quizEvaluateAttemptData ||
      errorEvaluateQuizAttempt ||
      isLoadingEvaluateQuizAttempt
    )
      return;

    setIsQuizReady(false);
  }, [
    quizEvaluateAttemptData,
    isLoadingEvaluateQuizAttempt,
    errorEvaluateQuizAttempt,
  ]);

  const ResumeModalView: JSX.Element | null = useResumeElearning(
    setCurrentFrameId,
    elearningData?.name,
    showResumeModal,
  );
  const { isMobile } = useIsMobileScreenSize();

  if (!cid || !mid || !token) {
    return <div> Invalid request please use the correct link. </div>;
  }

  if (error) {
    return <div> Something went wrong. Please contact the administrator. </div>;
  }

  /* TODO: add unit test for logic flow */
  if (
    !manifestData?.Item ||
    !isSuccess ||
    !elearningData ||
    !elearningData.frames ||
    !quizData ||
    (!liveQuizAttemptData && quizData.quizMetadata.lmsTrackingEnable) ||
    elearningBehaviourSettingsIsLoading ||
    isLoading
  ) {
    return <Spinner />;
  }

  const playerHandlerState = {
    questionsToAsk: quizData.quizMetadata.questionNo,
    questionsAsked: 0,
    correctAnswers: 0,
    modifiedQuestions: quizData.questions,
    questionsCompleted: [],
    hasStarted: false,
  };

  const playerHandlers = {
    submitResult: (result: any, callback: () => void) => {
      const score = Math.round(
        (playerHandlerState.correctAnswers / quizData.quizMetadata.questionNo) *
          100,
      );
      const liveAssessmentTracking: LiveAssessmentTrackingType = {
        responseDetails: {
          questionCompleted: playerHandlerState.questionsCompleted,
          quizId: quizData.id,
          pass: quizData.quizMetadata.pass,
          score,
        },
        isCompleted: score >= quizData.quizMetadata.pass,
        elearningId,
        courseId: parseInt(cid, 10),
        courseModuleId: parseInt(mid, 10),
        token,
        assessmentTrackingId: result?.assessmentTrackingId ?? '',
        attemptId: result?.attemptId ? Number(result?.attemptId) : 0,
      };

      submitQuizResult(liveAssessmentTracking);
      playerHandlerState.hasStarted = false;
    },
    submitQuestion: (
      answer: AnswerDataType,
      callback: (payload: any) => void,
    ) => {
      const nextPayload = processQuestion(answer, playerHandlerState);
      callback(nextPayload);
    },
    getQuestions: () => {
      playerHandlerState.questionsAsked = 0;
      playerHandlerState.correctAnswers = 0;
      playerHandlerState.modifiedQuestions = quizData.questions;
      playerHandlerState.questionsCompleted = [];
    },
    submitAssessment: (
      assessmentData: any,
      callback: (payload: { score: number }) => void,
    ) => {
      const score = Math.round(
        (playerHandlerState.correctAnswers / quizData.quizMetadata.questionNo) *
          100,
      );

      callback({ score });
    },
    startAssessment: (
      getAttemptData: QuizAttemptResponseType | null | undefined,
    ) => {
      let remainingQuizAttempt = getAttemptData?.remainingQuizAttempt ?? 0;

      // If tracking or the feature flag is disabled, set remaining attempts to 1
      const isTrackingEnabled =
        quizData.quizMetadata.lmsTrackingEnable && isQuizAttemptEnabled;

      if (!isTrackingEnabled) {
        remainingQuizAttempt = 1;
      }

      if (remainingQuizAttempt > 0) {
        if (!playerHandlerState.hasStarted) {
          playerHandlerState.hasStarted = true;
          // Call Quiz Start
          startQuizAttempt({
            courseModuleId: Number(mid),
            courseId: Number(cid),
            elearningId,
            token,
            quizMetadata: quizData.quizMetadata,
          });

          setTimeout(() => {
            playerHandlerState.hasStarted = false;
          }, 2000);
        }
      } else {
        evaluateQuizAttempt({
          courseModuleId: Number(mid),
          courseId: Number(cid),
          elearningId,
          token,
          quizMetadata: quizData.quizMetadata,
        });
      }
    },
    moveToFrame: (nextFrameId: string) => {
      setCurrentFrameId(nextFrameId);
      const nextPath = `${generatePath(LiveRoutes.path, {
        tenantId,
        elearningId,
        type,
        frameId: nextFrameId,
      })}?${query.toString()}`;
      history.push(nextPath);

      // If opened in iFrame, pass the frame ID to the parent window
      if (window.parent != null && window.self !== window.parent) {
        const parentOrigin = window.self.location.origin.split('.');
        parentOrigin.splice(1, 1);
        window.parent.postMessage(
          `frameId=${nextFrameId}`,
          parentOrigin.join('.'),
        );
      }
    },
  };

  const publishedAssetLink = getPublishedEntityLocation({
    contentType: 'published-content-assets',
    tenantId,
    type,
    entity: 'elearnings',
    entityId: elearningId,
    identifier: manifestData.Item.elearningIdentifier,
  });

  // transformElearningJson (publishedEntities.ts) already rewrote all frame
  // HTML; this per-page rewrite is now a no-op, kept only until it is removed
  // from all five player pages together.
  const frames = elearningData.frames.map((frame) => {
    const content = replaceContentAssetLink({
      content: frame.content,
      newLink: publishedAssetLink,
      tenantId,
      elearningId,
    });
    return { ...frame, content };
  });

  const elearning = { ...elearningData };
  if (!elearning.theme) {
    elearning.theme = appData.playerTheme.default;
  }

  return (
    <>
      {ResumeModalView}
      <PlayerUi
        isPlayable={true}
        isLive={true}
        currentFrameId={currentFrameId || frames[0].id}
        isQuizEnabled={isQuizReady}
        elearning={elearning}
        frames={frames}
        quiz={quizData}
        resources={elearning.resources ?? []}
        helperContent={
          helpData
            ? helpData.content
            : (appData.helpJson.default.content as HelperType[])
        }
        helperFaq={
          helpData
            ? helpData.faqs
            : (appData.helpJson.default.faqs as FaqType[])
        }
        handlers={playerHandlers}
        isMobileView={isMobile}
        vocamVideoLink={vocamVideoLink}
        elearningBehaviourSettings={elearningBehaviourSettingsData}
        quizStartAttemptData={quizStartAttemptInfo}
        quizGetAttemptData={liveQuizAttemptData}
      />
    </>
  );
};

export default Live;
